import { Lightning } from '@lightningjs/sdk';
import { TRANSPARENT } from '../utils/colors';

export default class ImageShader extends Lightning.shaders.WebGLDefaultShader {
  constructor(context) {
    super(context);
    this._radius = 0;
    this._scale = 1.0;
    this._progress = 0.0;
    this._progressBarColorRaw = TRANSPARENT;
    this._progressBackgroundColorRaw = TRANSPARENT;
    this._overlayColorRaw = TRANSPARENT;
    this._progressBarColor = this._getNormalizedColor(this._progressBarColorRaw);
    this._progressBackgroundColor = this._getNormalizedColor(this._progressBackgroundColorRaw);
    this._overlayColor = this._getNormalizedColor(this._overlayColorRaw);
    this._progressHeight = 0;
    // Reused across frames: setupUniforms runs once per tile per frame, and Lightning clones the value it uploads,
    // so allocating here instead of there keeps a per-tile allocation out of the render loop
    this._resolution = new Float32Array(2);
  }

  set scale(v) {
    const scale = Math.max(v, 0);

    if (this._scale === scale) {
      return;
    }

    this._scale = scale;
    this.redraw();
  }

  get scale() {
    return this._scale;
  }

  set radius(v) {
    if (this._radius === v) {
      return;
    }

    this._radius = v;
    this.redraw();
  }

  get radius() {
    return this._radius;
  }

  set progressBarColor(color) {
    if (this._progressBarColorRaw === color) {
      return;
    }

    this._progressBarColorRaw = color;
    this._progressBarColor = this._getNormalizedColor(color);
    this.redraw();
  }

  get progressBarColor() {
    return this._progressBarColorRaw;
  }

  set progressBackgroundColor(color) {
    if (this._progressBackgroundColorRaw === color) {
      return;
    }

    this._progressBackgroundColorRaw = color;
    this._progressBackgroundColor = this._getNormalizedColor(color, false);
    this.redraw();
  }

  get progressBackgroundColor() {
    return this._progressBackgroundColorRaw;
  }

  get overlayColor() {
    return this._overlayColorRaw;
  }

  set overlayColor(color) {
    if (this._overlayColorRaw === color) {
      return;
    }

    this._overlayColorRaw = color;
    this._overlayColor = this._getNormalizedColor(color, false);
    this.redraw();
  }

  set progressHeight(value) {
    if (this._progressHeight === value) {
      return;
    }

    this._progressHeight = value;
    this.redraw();
  }

  get progressHeight() {
    return this._progressHeight;
  }

  set progress(num) {
    if (this._progress === num) {
      return;
    }

    this._progress = num;
    this.redraw();
  }

  get progress() {
    return this._progress;
  }

  /**
   * Without a corner radius to mask, a progress bar to draw and an overlay to blend, every branch of the fragment
   * shader is skipped and what is left is the texture times the vertex colour — which is what the default shader
   * does. Saying so lets Lightning swap this shader out for the default one, which drops the per-tile uniform
   * upload and the program switch, and cuts the fragment cost of a tile to a single texture fetch.
   *
   * It pays off broadly because the corner radius is zero for every customer but rtlde, and most templates never
   * set an overlay colour, so an unfocused tile with nothing watched on it needs none of the work below.
   *
   * @returns {boolean} whether this shader would leave the tile untouched
   */
  useDefault() {
    return this._radius === 0 && this._progress === 0 && this._overlayColor[3] === 0;
  }

  _getNormalizedColor(color, useAlphaChannel = true) {
    const col = Lightning.StageUtils.getRgbaComponentsNormalized(color);
    if (useAlphaChannel) {
      col[0] *= col[3];
      col[1] *= col[3];
      col[2] *= col[3];
    }

    return new Float32Array(col);
  }

  setupUniforms(operation) {
    super.setupUniforms(operation);
    const owner = operation.shaderOwner;
    const renderPrecision = this.ctx.stage.getRenderPrecision();
    this._resolution[0] = owner._w * renderPrecision;
    this._resolution[1] = owner._h * renderPrecision;
    this._setUniform('radius', this._radius, this.gl.uniform1f);
    this._setUniform('progressColor', this._progressBackgroundColor, this.gl.uniform4fv);
    this._setUniform('progressBarColor', this._progressBarColor, this.gl.uniform4fv);
    this._setUniform('overlayColor', this._overlayColor, this.gl.uniform4fv);
    this._setUniform('scale', this._scale, this.gl.uniform1f);
    this._setUniform('progress', this._progress, this.gl.uniform1f);
    this._setUniform('progressHeight', this._progressHeight, this.gl.uniform1f);
    this._setUniform('resolution', this._resolution, this.gl.uniform2fv);
  }
}

ImageShader.vertexShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif
    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;

    attribute vec4 aColor;
    uniform vec2 projection;
    varying vec2 vTextureCoord;
    varying vec4 vColor;

    void main(void){
        gl_Position = vec4(aVertexPosition.x * projection.x - 1.0, aVertexPosition.y * -abs(projection.y) + 1.0, 0.0, 1.0);
        vTextureCoord = aTextureCoord;
        vColor = aColor;
        gl_Position.y = -sign(projection.y) * gl_Position.y;
    }
`;

ImageShader.fragmentShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    varying vec2 vTextureCoord;
    varying vec4 vColor;

    uniform sampler2D uSampler;
    uniform vec2 resolution;
    uniform float radius;
    uniform float scale;
    uniform float progress;
    uniform float progressHeight;
    uniform vec4 progressBarColor;
    uniform vec4 progressColor;
    uniform vec4 overlayColor;

    void main() {
        vec4 color = texture2D(uSampler, vTextureCoord) * vColor;

        // Darkens the top and the bottom of the tile so the metadata drawn over it stays legible
        if (overlayColor.a > 0.0) {
          float top = clamp(1.0 - vTextureCoord.y * 4.0, 0.0, 1.0) * 0.3;
          float bottom = clamp(vTextureCoord.y * 2.0 - 1.0, 0.0, 1.0) * 0.6;

          color.rgb = mix(color.rgb, overlayColor.rgb, top + bottom + 0.1);
        }

        // Rounded corner coverage, as a signed distance to the tile box. The radius is divided by the focus scale so
        // a zoomed tile keeps the corner it had at rest instead of a proportionally bigger one
        vec2 halfRes = resolution * 0.5;
        float cornerRadius = radius / scale;
        vec2 corner = abs(vTextureCoord * resolution - halfRes) - (halfRes + 0.5 - cornerRadius);
        float distance = min(max(corner.x, corner.y), 0.0) + length(max(corner, 0.0)) - cornerRadius;
        float mask = clamp(-distance, 0.0, 1.0);

        color *= mask;

        if (progress > 0.0) {
          // The bar is laid out in unscaled tile space, so the offsets keep it inside a focus-zoomed tile
          float halfOffset = (scale - 1.0) * 0.5;
          vec2 scaled = vTextureCoord * scale;
          float barTop = 1.0 - progressHeight / resolution.y + halfOffset;
          float inBar =
            step(barTop, scaled.y) *
            step(halfOffset, scaled.x) *
            (1.0 - step(1.0 + halfOffset, scaled.x)) *
            (1.0 - step(1.0 + halfOffset, scaled.y)) *
            mask;

          color.rgb = mix(color.rgb, progressColor.rgb, progressColor.a * inBar);
          color = mix(color, progressBarColor, inBar * (1.0 - step(progress + halfOffset, scaled.x)));
        }

        gl_FragColor = vec4(color.rgb, mask);
    }
`;
